import { Carrier, CoverageType, InsuranceType, MapperCreateRequest, MapperEntity, MapperSchemaCreateRequest, Policy } from "@bluefalcon/clients-management-client-fetch";
import { Box, Button, Text, Checkbox, Flex, FormControl, FormLabel, Grid, Heading, Input, Stack, Switch, Textarea } from "@chakra-ui/react";
import { ChangeEvent, useEffect, useState } from "react";
import { Dropdown } from "../../components/dropdown/dropdown.comp";
import { carriersService, mappersService } from "../../services";
import { insuranceTypes } from "../../constants/insurace-type.constants";
import { coverageTypes } from "../../constants/coverage-type.constants";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "../../components/base/loading-spinner/loading-spinner.comp";
import { mapperEntity } from "../../constants/mapper-entity.constants";
import { SchemaConnection } from "./schema-connection.comp";
import { PolicyConnection } from "./policy-connection.comp";
import { toast } from "../../utils/toast.utils";
import { MetadataConnection } from "./metadata-connection.comp";

export function AddMapper() {
    const navigate = useNavigate();
    const { mapperId } = useParams();
    const [formData, setFormData] = useState<Partial<Omit<MapperCreateRequest, 'config'>>>({});
    const [selectedCarrier, setSelectedCarrier] = useState<Carrier>();
    const [carriers, setCarriers] = useState<Carrier[]>([]);
    const [selectedInsuranceTypes, setSelectedInsuranceTypes] = useState<InsuranceType[]>([]);
    const [selectedCoverageTypes, setSelectedCoverageTypes] = useState<CoverageType[]>([]);
    const [sheetIndex, setSheetIndex] = useState<number>();
    const [headerIndex, setHeaderIndex] = useState<number>();
    const [startRow, setStartRow] = useState<number>();
    const [skipBottomRows, setSkipBottomRows] = useState<number>();
    const [policyNameColumns, setPolicyNameColumns] = useState<string>();
    const [dateFormat, setDateFormat] = useState('M/dd/yyyy');
    const [columns, setColumns] = useState<MapperSchemaCreateRequest[]>([]);
    const [metadata, setMetadata] = useState<MapperSchemaCreateRequest[]>([]);
    const [policyMap, setPolicyMap] = useState<MapperSchemaCreateRequest[]>([]);
    const [policies, setPolicies] = useState<Policy[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const { results } = await carriersService.searchCarriers();
            setCarriers(results);
            if (mapperId) {
                const res = await mappersService.getMapperById(Number(mapperId));
                const carr = results.find((carr) => carr.id === res.carrierId);
                setSelectedCarrier(carr);
                setDateFormat(res.dateFormat);
                setHeaderIndex(res.headerIndex);
                setSheetIndex(res.sheetIndex);
                setColumns(res.schemas.filter((schm) => schm.type === 'columns'));
                setPolicyMap(res.schemas.filter((schm) => schm.type === 'policies'));
                setMetadata(res.schemas.filter((schm) => schm.type === 'metadata'));
                setSelectedInsuranceTypes(res.insuranceTypes);
                setSelectedCoverageTypes(res.coverageTypes);
                setStartRow(res.startRow);
                setSkipBottomRows(res.skipBottomRows);
                setPolicyNameColumns(res.policyColumnNames?.join(', ') ?? '');
                setFormData({
                    active: res.active,
                    description: res.description,
                    entityType: res.entityType,
                    name: res.name,
                });
            }
        } catch (error) {
            toast('Something went wrong getting data. Please try again later', 'error');
            navigate('/');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleInsuranceTypeCheckboxChange = (key: InsuranceType) => {
        setSelectedInsuranceTypes((prevSelected) => {
            if (prevSelected.includes(key)) {
                return prevSelected.filter((item) => item !== key);
            } else {
                return [...prevSelected, key];
            }
        });
    };

    const handleCoverageTypeCheckboxChange = (key: CoverageType) => {
        setSelectedCoverageTypes((prevSelected) => {
            if (prevSelected.includes(key)) {
                return prevSelected.filter((item) => item !== key);
            } else {
                return [...prevSelected, key];
            }
        });
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            if (formData?.name?.trim() && selectedCarrier) {
                if (mapperId) {
                    await mappersService.updateMapper(Number(mapperId), {
                        ...formData,
                        active: formData.active ?? true,
                        entityType: formData.entityType ?? MapperEntity.BookOfBusiness,
                        name: formData.name,
                        description: formData.description ?? '',
                        coverageTypes: selectedCoverageTypes,
                        insuranceTypes: selectedInsuranceTypes,
                        headerIndex,
                        policyColumnNames: policyNameColumns?.split(',').map((s) => s.trim()) ?? [],
                        skipBottomRows: skipBottomRows ?? 0,
                        startRow: startRow ?? 0,
                        sheetIndex,
                        dateFormat,
                        schemas: [
                            ...policyMap.map((schm) => ({ ...schm, mapperId: Number(mapperId) })),
                            ...columns.map((schm) => ({ ...schm, mapperId: Number(mapperId) })),
                            ...metadata.map((schm) => ({ ...schm, mapperId: Number(mapperId) })),
                        ],
                    });
                    toast('Mapper Updated!');
                    navigate('/mappers');
                } else {
                    await mappersService.createMapper({
                        ...formData,
                        active: formData.active ?? true,
                        entityType: formData.entityType ?? MapperEntity.BookOfBusiness,
                        name: formData.name,
                        description: formData.description ?? '',
                        carrierId: selectedCarrier.id,
                        coverageTypes: selectedCoverageTypes,
                        insuranceTypes: selectedInsuranceTypes,
                        headerIndex: headerIndex ?? 0,
                        sheetIndex: sheetIndex ?? 0,
                        dateFormat,
                        policyColumnNames: policyNameColumns?.split(',').map((s) => s.trim()) ?? [],
                        skipBottomRows: skipBottomRows ?? 0,
                        startRow: startRow ?? 0,
                        schemas: [
                            ...policyMap.map((schm) => ({ ...schm, mapperId: undefined })),
                            ...columns.map((schm) => ({ ...schm, mapperId: undefined })),
                            ...metadata.map((schm) => ({ ...schm, mapperId: undefined })),
                        ],
                    });
                    toast('Mapper Created!');
                    navigate('/mappers');
                }
            }
        } catch (error) {
            toast('Something went wrong', 'error');
        } finally {
            setIsLoading(false);
        }
    }

    const handleCarrierDropdownChange = (carrierName?: string) => {
        setSelectedCarrier(carriers.find((c) => c.name === carrierName));
    };

    useEffect(() => {
        if (selectedCarrier) {
            const policies = selectedCarrier.policies.filter((p) => {
                const isCorrectCoverage = selectedCoverageTypes.includes(p.coverageType);
                const isCorrectInsurance = selectedInsuranceTypes.includes(p.insuranceType);
                return (isCorrectCoverage && isCorrectInsurance);
            });
            setPolicies(policies);
        } else {
            setPolicies([]);
        }
    }, [selectedCarrier, selectedCoverageTypes, selectedInsuranceTypes]);

    const getIsChecked = (type: 'insuranceType' | 'coverageType', key: InsuranceType | CoverageType): boolean => {
        if (type === 'coverageType') {
            const isChecked = selectedCoverageTypes.includes(key as CoverageType);
            return isChecked;
        }
        if (type === 'insuranceType') {
            return selectedInsuranceTypes.includes(key as InsuranceType);
        }
        return false;
    };

    if (isLoading) {
        return <LoadingSpinner />
    }

    return (
        <Box p={6} maxW="80%" mx="auto" boxShadow="md" borderRadius="md" bg="white">
            <Heading as="h1" size="lg" mb={6} textAlign="center">{`${mapperId ? 'Edit' : 'Add'} Mapper`}</Heading>

            <Box as="form" onSubmit={handleSubmit}>
                <Heading as="h2" size="md" mb={4}>General Information</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={6}>
                    <FormControl>
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <Input
                            id="name"
                            name="name"
                            value={formData?.name ?? ""}
                            onChange={handleChange}
                            placeholder="Enter mapper name"
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel htmlFor="description">Description</FormLabel>
                        <Textarea
                            id="description"
                            name="description"
                            value={formData?.description ?? ""}
                            onChange={handleChange}
                            placeholder="Enter description"
                        />
                    </FormControl>
                </Grid>

                <Heading as="h2" size="md" mb={4}>Settings</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={6}>
                    <FormControl display="flex" alignItems="center">
                        <FormLabel htmlFor="active" mb="0">Active:</FormLabel>
                        <Switch
                            id="active"
                            name="active"
                            isChecked={formData?.active ?? false}
                            onChange={(e) =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    active: e.target.checked,
                                }))
                            }
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel htmlFor="dateFormat">Date Format</FormLabel>
                        <Input
                            type="text"
                            name="dateFormat"
                            onChange={(e) => setDateFormat(e.target.value)}
                            value={dateFormat}
                        />
                    </FormControl>
                </Grid>

                <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={6}>
                    <Dropdown
                        placeholder="Select Carrier"
                        name="carrierId"
                        items={carriers.map((c) => c.name)}
                        onChange={handleCarrierDropdownChange}
                        value={selectedCarrier?.name}
                    />

                    <Dropdown
                        placeholder="Type"
                        name="entityType"
                        items={[MapperEntity.BookOfBusiness, MapperEntity.Commission].map((t) => mapperEntity[t])}
                        onChange={(t) => setFormData((prevFormData) => ({
                            ...prevFormData,
                            entityType: t ? Object.entries(mapperEntity).find(([key, value]) => t === value)?.[0] as MapperEntity : undefined
                        }))}
                        value={formData.entityType ? mapperEntity[formData.entityType] : undefined}
                    />
                </Grid>

                <Heading as="h2" size="md" mb={4}>Insurance & Coverage Types</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={6}>
                    <Stack spacing={3}>
                        {Object.entries(insuranceTypes).map(([key, value]) => (
                            <Checkbox
                                key={key}
                                value={value}
                                isChecked={getIsChecked('insuranceType', key as InsuranceType)}
                                onChange={() => handleInsuranceTypeCheckboxChange(key as InsuranceType)}
                            >
                                {value}
                            </Checkbox>
                        ))}
                    </Stack>

                    <Stack spacing={3}>
                        {Object.entries(coverageTypes).map(([key, value]) => (
                            <Checkbox
                                key={key}
                                value={value}
                                isChecked={getIsChecked('coverageType', key as CoverageType)}
                                onChange={() => handleCoverageTypeCheckboxChange(key as CoverageType)}
                            >
                                {value}
                            </Checkbox>
                        ))}
                    </Stack>
                </Grid>

                <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={6}>
                    <FormControl>
                        <FormLabel>Sheet Index</FormLabel>
                        <Input
                            type="number"
                            name="sheetIndex"
                            onChange={(e) => setSheetIndex(Number(e.target.value))}
                            value={sheetIndex}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Header Index</FormLabel>
                        <Input
                            type="number"
                            name="headerIndex"
                            onChange={(e) => setHeaderIndex(Number(e.target.value))}
                            value={headerIndex}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Start Row Index</FormLabel>
                        <Input
                            type="number"
                            name="startRow"
                            onChange={(e) => setStartRow(Number(e.target.value))}
                            value={startRow}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Skip Bottom Rows Count</FormLabel>
                        <Input
                            type="number"
                            name="skipBottomRows"
                            onChange={(e) => setSkipBottomRows(Number(e.target.value))}
                            value={skipBottomRows}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Policy Column Names</FormLabel>
                        <Text fontSize={'sm'}>{`(Separate with commas)`}</Text>
                        <Input
                            type="text"
                            name="policyColumnNames"
                            onChange={(e) => setPolicyNameColumns(e.target.value)}
                            value={policyNameColumns}
                        />
                    </FormControl>
                </Grid>

                {formData.entityType && <Box mb={6}>
                    <SchemaConnection columns={columns} onSave={(schema) => setColumns(schema)} />
                </Box>}

                {selectedCarrier && selectedCoverageTypes.length > 0 && selectedInsuranceTypes.length > 0 && (
                    <Box mb={6}>
                        <PolicyConnection policyMap={policyMap} onSave={(policyMap) => setPolicyMap(policyMap)} policies={policies} />
                    </Box>
                )}

                {formData.entityType && <Box mb={6}>
                    <MetadataConnection metadata={metadata} onSave={(schema) => setMetadata(schema)} />
                </Box>}

                <Flex>
                    <Button ml="auto" colorScheme="teal" type="submit">{mapperId ? 'Save Changes' : 'Create Mapper'}</Button>
                </Flex>
            </Box>
        </Box>
    );
}
